import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaShieldAlt, 
  FaGlobe, 
  FaFileContract, 
  FaGavel, 
  FaFileSignature, 
  FaHandshake, 
  FaLock,
  FaLightbulb 
} from 'react-icons/fa';

const Section = styled.section`
  padding: 6rem 6%;
  background: linear-gradient(to bottom, #fafafa, #f2f2f2);
  
  @media (max-width: 768px) {
    padding: 4rem 6%;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #555;
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  padding-bottom: 1.5rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 70px;
    height: 3px;
    background: linear-gradient(90deg, #333, #999);
    border-radius: 3px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2.5rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  border: 1px solid rgba(0, 0, 0, 0.03);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 0;
    background: linear-gradient(to bottom, #333, #999);
    transition: height 0.5s ease;
    z-index: 1;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    
    &:before {
      height: 100%;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 2rem 2rem 1rem;
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f5f5f5, #fff);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 1.25rem;
  font-size: 1.6rem;
  color: #333;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(45deg);
    transition: all 0.6s ease;
    opacity: 0;
  }
  
  ${ServiceCard}:hover & {
    transform: scale(1.1) rotate(-5deg);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    background: linear-gradient(135deg, #333, #555);
    color: white;
    
    &:before {
      opacity: 1;
      transform: rotate(45deg) translate(150%, 150%);
    }
  }
`;

const ServiceName = styled.h3`
  font-size: 1.4rem;
  color: #222;
  margin: 0;
  transition: all 0.3s ease;
  font-weight: 600;
  
  ${ServiceCard}:hover & {
    transform: translateX(8px);
    color: #000;
  }
`;

const ServiceContent = styled.div`
  padding: 0.5rem 2rem 2rem;
  flex: 1;
`;

const ServiceCategory = styled.span`
  display: inline-block;
  background: #f5f5f5;
  color: #666;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: #666;
  margin: 0;
  line-height: 1.7;
  font-size: 0.95rem;
`;

// Service card component to avoid repetition
const ServiceItem = ({ icon, title, category, description, delay }) => {
  return (
    <ServiceCard 
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.02 }}
    >
      <CardHeader>
        <IconWrapper>
          {icon}
        </IconWrapper>
        <ServiceName>{title}</ServiceName>
      </CardHeader>
      <ServiceContent>
        <ServiceCategory>{category}</ServiceCategory>
        <ServiceDescription>{description}</ServiceDescription>
      </ServiceContent>
    </ServiceCard>
  );
};

const Services = () => {
  const services = [
    {
      title: 'Product Liability Protection',
      category: 'Risk Management',
      description: 'Our comprehensive liability protection framework safeguards your products and services from potential claims. We implement robust legal structures that mitigate risks associated with product deployment in the marketplace.',
      icon: <FaShieldAlt />
    },
    {
      title: 'Cross-Border Contracts',
      category: 'International',
      description: 'Expertly crafted agreements that seamlessly navigate multiple jurisdictions, ensuring enforceability across borders. Our contracts account for regional legal variations while maintaining consistent terms and protections for your business operations worldwide.',
      icon: <FaGlobe />
    },
    {
      title: 'Strategic Licensing',
      category: 'Intellectual Property',
      description: 'Maximize the value of your proprietary assets through carefully structured licensing agreements. We develop licensing strategies that protect your intellectual property while creating sustainable revenue streams and strategic partnerships.',
      icon: <FaFileContract />
    },
    {
      title: 'Dispute Resolution',
      category: 'Litigation',
      description: 'Efficient and effective resolution of commercial disagreements through sophisticated negotiation, arbitration, or litigation strategies. Our approach prioritizes business continuity and reputation preservation while vigorously defending your interests.',
      icon: <FaGavel />
    },
    {
      title: 'Confidentiality & Agreements',
      category: 'Compliance',
      description: 'Comprehensive suite of essential business agreements including NDAs, LOIs, MOUs, and other critical documentation. These foundational documents establish clear terms and protections during negotiations, partnerships, and early-stage business relationships.',
      icon: <FaFileSignature />
    },
    {
      title: 'Strategic Negotiations',
      category: 'Business Development',
      description: 'Expert representation during high-stakes business discussions to secure advantageous terms. Our negotiation specialists combine legal expertise with business acumen to achieve outcomes that support your strategic objectives and growth trajectory.',
      icon: <FaHandshake />
    },
    {
      title: 'Data Privacy Compliance',
      category: 'Regulatory',
      description: 'Navigate complex regulatory frameworks including GDPR, CCPA, and emerging privacy standards with confidence. We implement comprehensive compliance protocols that protect consumer data while enabling efficient business operations.',
      icon: <FaLock />
    },
    {
      title: 'IP Strategy & Protection',
      category: 'Intellectual Property',
      description: 'Comprehensive intellectual property strategies to identify, protect, and monetize your innovations. We secure patents, trademarks, and copyrights while developing enforcement mechanisms that safeguard your competitive advantage in the marketplace.',
      icon: <FaLightbulb />
    }
  ];

  return (
    <Section id="services">
      <SectionHeader>
        <SectionTitle>Our Services</SectionTitle>
        <SectionSubtitle>
          Sophisticated legal solutions for ambitious startups and growing enterprises.
        </SectionSubtitle>
      </SectionHeader>
      
      <ServicesGrid>
        {services.map((service, index) => (
          <ServiceItem 
            key={index}
            icon={service.icon}
            title={service.title}
            category={service.category}
            description={service.description}
            delay={index * 0.1}
          />
        ))}
      </ServicesGrid>
    </Section>
  );
};

export default Services; 